import { useState, useEffect, useRef } from 'react';
import { Link, Route, useNavigate } from 'react-router-dom';
import { FaMapMarkerAlt, FaIndustry, FaUserTie, FaHourglassHalf, FaLaptopHouse, FaSearch, FaArrowRight } from 'react-icons/fa';
import Navbar from './Navbar';
import Footer from './Footer';
import data from '../seo/landingData';

import { useSeo } from './SeoLanding';

import imgSoftware from '../assets/software.jpg';
import imgDoctor from '../assets/doctor.jpg';
import imgBank from '../assets/bank.jpg';
import imgBpo from '../assets/bpo.jpg';
import imgManufacturing from '../assets/manufacturing.jpg';
import imgLogistics from '../assets/logistic.jpg';
import imgHotel from '../assets/hotel.jpg';
import imgTeacher from '../assets/teacher.jpg';
import imgHr from '../assets/HR.jpg';
import imgSales from '../assets/sales.jpg';
import imgMarketing from '../assets/marketing.jpg';
import imgConstruction from '../assets/constructour.jpg';
import imgBengaluru from '../assets/bengaluru.jpg';
import imgDelhi from '../assets/delhi.jpg';
import imgHyderabad from '../assets/hydrabad.jpg';
import imgKolkata from '../assets/kolkat.jpg';
import imgMumbai from '../assets/mumbi.jpg';

const { CITIES, INDUSTRIES, TITLES, EXPERIENCE, WORK } = data;

// First match wins. Add a line here for any new city or industry photo.
const IMAGE_RULES = [
  [/bengaluru|bangalore/, imgBengaluru],
  [/delhi/, imgDelhi],
  [/hyderabad/, imgHyderabad],
  [/kolkata/, imgKolkata],
  [/mumbai/, imgMumbai],
  [/construction|civil|site engineer/, imgConstruction],
  [/information technology|software|developer|programmer|\bit\b/, imgSoftware],
  [/health|doctor|nurse|medical|hospital|pharma/, imgDoctor],
  [/bank|financ|account/, imgBank],
  [/bpo|customer|support|call cent/, imgBpo],
  [/manufactur|production|factory/, imgManufacturing],
  [/logistic|supply|delivery|driver|warehouse/, imgLogistics],
  [/hospitality|retail|hotel|chef/, imgHotel],
  [/educat|teacher|admin/, imgTeacher],
  [/human resource|\bhr\b|recruit/, imgHr],
  [/sales|business development/, imgSales],
  [/marketing|seo|content/, imgMarketing],
];

const imageFor = (name = '') => {
  const n = name.toLowerCase();
  const hit = IMAGE_RULES.find(([re]) => re.test(n));
  return hit ? hit[1] : null;
};

/* Four hub pages linked from the Footer "Explore" column:
     /jobs-by-location, /jobs-by-industry, /jobs-by-experience, /popular-job-titles
   They list pages that already exist in landingData.js, so no new copy is needed.
   App.jsx:  import { seoHubRoutes } from './pages/SeoHubs';  ...  {seoHubRoutes} */

const SITE = process.env.REACT_APP_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

const HUBS = [
  {
    path: '/jobs-by-location', name: 'Location', Icon: FaMapMarkerAlt, bg: imgMumbai,
    h1: 'Find Jobs by Location',
    title: 'Jobs by Location – Browse Vacancies in Every City | PlanB Jobs',
    description: 'Browse job vacancies city by city. Pick a location to see current openings, hiring industries and local career tips on PlanB Jobs.',
    intro: 'Choose a city to see its current openings, the industries hiring there and tips for job seekers in that market.',
    placeholder: 'Search a city',
    groups: [{ title: 'Cities', noun: 'cities', Icon: FaMapMarkerAlt, items: CITIES, label: (i) => `Jobs in ${i.name}` }],
  },
  {
    path: '/jobs-by-industry', name: 'Industry', Icon: FaIndustry, bg: imgSoftware,
    h1: 'Find Jobs by Industry',
    title: 'Jobs by Industry – Browse Vacancies in Every Sector | PlanB Jobs',
    description: 'Explore job openings by industry. See popular job titles, skills employers ask for and live vacancies for each sector on PlanB Jobs.',
    intro: 'Pick an industry to see live openings, the titles employers hire for and the skills they ask for.',
    placeholder: 'Search an industry',
    groups: [{ title: 'Industries', noun: 'industries', Icon: FaIndustry, items: INDUSTRIES, label: (i) => `${i.name} jobs` }],
  },
  {
    path: '/jobs-by-experience', name: 'Experience', Icon: FaHourglassHalf, bg: imgSales,
    h1: 'Find Jobs by Experience Level',
    title: 'Jobs by Experience – Fresher, Experienced and Remote Jobs | PlanB Jobs',
    description: 'Find jobs that match your experience level, from fresher roles to senior positions, plus remote, hybrid and work from home openings.',
    intro: 'Start with your experience level or preferred way of working, and see openings that fit.',
    placeholder: 'Search by experience or work mode',
    groups: [
      { title: 'By experience', noun: 'pages', Icon: FaHourglassHalf, items: EXPERIENCE, label: (i) => i.name },
      { title: 'By work mode', noun: 'pages', Icon: FaLaptopHouse, items: WORK, label: (i) => i.name },
    ],
  },
  {
    path: '/popular-job-titles', name: 'Job title', Icon: FaUserTie, bg: imgHr,
    h1: 'Popular Job Titles',
    title: 'Popular Job Titles – Browse Jobs by Role | PlanB Jobs',
    description: 'Browse jobs by popular job title. Read what each role involves, the skills needed and see current openings on PlanB Jobs.',
    intro: 'Pick a role to see what it involves, the skills employers look for and current openings.',
    placeholder: 'Search a job title',
    groups: [{ title: 'Job titles', noun: 'titles', Icon: FaUserTie, items: TITLES, label: (i) => i.name }],
  },
];

function Reveal({ children, delay = 0, className = '', variant = 'up' }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) { setShown(true); return; }
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setShown(true); io.disconnect(); } },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`reveal ${variant !== 'up' ? `reveal-${variant}` : ''} ${shown ? 'reveal-in' : ''} ${className}`}
    >
      {children}
    </div>
  );
}

function ScrollProgress() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setP(h > 0 ? (window.scrollY / h) * 100 : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <div className="fixed top-0 left-0 h-[3px] z-[100] brand-gradient" style={{ width: `${p}%` }} aria-hidden="true" />;
}

function Hub({ hub }) {
     const [query, setQuery] = useState('');
   const navigate = useNavigate();
   const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate('/'));
  const canonical = `${SITE}${hub.path}`;
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: 'Jobs', item: `${SITE}/jobs` },
        { '@type': 'ListItem', position: 3, name: hub.h1, item: canonical },
      ],
    },
  ];
  useSeo({ title: hub.title, description: hub.description, canonical, noindex: false, jsonLd });

  const q = query.trim().toLowerCase();
  const groups = hub.groups
    .map((g) => ({ ...g, shown: q ? g.items.filter((i) => i.name.toLowerCase().includes(q)) : g.items }))
    .filter((g) => g.shown.length > 0);

  return (
    <div
      className="min-h-screen bg-[#FBF9FE] overflow-x-clip text-[#1E0F3D]"
      style={{
        backgroundImage: `linear-gradient(rgba(251,249,254,.9), rgba(251,249,254,.9)), url(${hub.bg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      <ScrollProgress />
<Navbar />

      {/* HERO */}
   {/* HERO */}
<header className="relative overflow-hidden border-b border-purple-100">
  <img src={hub.bg} alt="" className="absolute inset-0 h-full w-full object-cover" aria-hidden="true" />
  <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/65 to-[#FBF5FF]/90" aria-hidden="true" />
  <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
    <div className="absolute -top-32 -right-20 w-[28rem] h-[28rem] rounded-full bg-[#7C3AED]/15 blur-3xl float-slow" />
    <div className="absolute -bottom-32 -left-20 w-[24rem] h-[24rem] rounded-full bg-[#DB2777]/12 blur-3xl float-slower" />
    <div className="absolute inset-0 opacity-60" style={{ backgroundImage: 'radial-gradient(rgba(124,58,237,0.10) 1px, transparent 1px)', backgroundSize: '26px 26px' }} />
  </div>

  <div className="relative z-10 max-w-4xl mx-auto px-5 lg:px-8 pt-5 pb-6 lg:pt-6 lg:pb-7 text-center">
        <div className="rise flex justify-start mb-3">
      <button
        type="button"
        onClick={goBack}
        className="group inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-purple-100 text-sm font-semibold text-[#7C3AED] shadow-sm transition-all duration-300 hover:-translate-x-0.5 hover:bg-[#F5F0FA] hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/50"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1 motion-reduce:transition-none" aria-hidden="true">
          <path d="M19 12H5M11 6l-6 6 6 6" />
        </svg>
        Back
      </button>
    </div>

    <nav aria-label="Breadcrumb" className="rise text-xs font-medium text-gray-700 mb-3">
      <ol className="flex flex-wrap items-center justify-center gap-1.5">
        <li><Link to="/" className="hover:text-[#7C3AED]">Home</Link></li>
        <li aria-hidden="true">/</li>
        <li><Link to="/jobs" className="hover:text-[#7C3AED]">Jobs</Link></li>
        <li aria-hidden="true">/</li>
        <li className="font-semibold text-[#1E0F3D]" aria-current="page">{hub.h1}</li>
      </ol>
    </nav>

    <div className="rise mb-2" style={{ animationDelay: '.08s' }}>
      <span className="bob inline-flex w-11 h-11 rounded-2xl brand-gradient text-white items-center justify-center text-xl shadow-lg shadow-[#7C3AED]/30">
        <hub.Icon aria-hidden="true" />
      </span>
    </div>

    <h1
      key={hub.path}
      className="rise shimmer-text mx-auto mb-3 w-fit max-w-full text-[32px] sm:text-5xl font-extrabold leading-[1.1] tracking-tight bg-gradient-to-r from-[#5B21B6] via-[#BE185D] to-[#5B21B6] bg-clip-text text-transparent"
      style={{ animationDelay: '.14s' }}
    >
      {hub.h1}
    </h1>
    <p className="rise max-w-2xl mx-auto text-base lg:text-lg leading-7 text-gray-600" style={{ animationDelay: '.22s' }}>
      {hub.intro}
    </p>

    {/* Search */}
    <div className="rise relative max-w-xl mx-auto mt-4" style={{ animationDelay: '.3s' }}>
      <label htmlFor="hub-search" className="sr-only">{hub.placeholder}</label>
      <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" aria-hidden="true" />
      <input
        id="hub-search" type="search" value={query} onChange={(e) => setQuery(e.target.value)}
        placeholder={hub.placeholder}
        className="w-full rounded-full border border-purple-100 bg-white pl-11 pr-5 py-3.5 text-sm shadow-sm transition duration-300 focus:outline-none focus:border-[#7C3AED]/50 focus:shadow-[0_12px_30px_-10px_rgba(124,58,237,0.45)] focus:ring-2 focus:ring-[#7C3AED]/30"
      />
    </div>

    {/* Switch between hubs */}
    <nav aria-label="Browse jobs by" className="mt-4 flex flex-wrap items-center justify-center gap-2">
      {HUBS.map((h, idx) => {
        const active = h.path === hub.path;
        return (
          <Link
            key={h.path} to={h.path} aria-current={active ? 'page' : undefined}
            style={{ animationDelay: `${0.38 + idx * 0.07}s` }}
            className={`rise inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition duration-300 hover:-translate-y-0.5 ${
              active ? 'brand-gradient text-white shadow-lg shadow-[#7C3AED]/30' : 'bg-white text-[#7C3AED] border border-purple-100 hover:bg-[#F5F0FA] hover:shadow-md'
            }`}
          >
            <h.Icon className="text-xs" aria-hidden="true" /> {h.name}
          </Link>
        );
      })}
    </nav>
  </div>
</header>

      {/* LISTS */}
      <main className="max-w-6xl mx-auto px-5 lg:px-8 py-6 lg:py-8 space-y-8">
        {groups.length === 0 && (
          <div className="rounded-2xl bg-white border border-purple-100 px-6 py-10 text-center">
            <p className="font-bold text-lg mb-1">Nothing matches “{query}”.</p>
            <p className="text-sm text-gray-500 mb-5">Check the spelling, or clear the search to see everything.</p>
            <button type="button" onClick={() => setQuery('')} className="brand-gradient text-white px-6 py-2.5 rounded-xl text-sm font-semibold">Clear search</button>
          </div>
        )}

       {groups.map((g) => (
  <section key={g.title} aria-labelledby={`hub-${g.title}`}>
    <Reveal>
      <div className="flex items-baseline justify-between gap-3 mb-5">
        <h2 id={`hub-${g.title}`} className="text-xl font-bold">{g.title}</h2>
        <span className="text-sm font-semibold text-[#7C3AED] bg-[#F5F0FA] px-3 py-1 rounded-full">
          {g.shown.length} {g.shown.length === 1 ? g.noun.replace(/ies$/, 'y').replace(/s$/, '') : g.noun}
        </span>
      </div>
    </Reveal>

    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {g.shown.map((i, idx) => (
        <li key={i.path} className="h-full">
          <Reveal delay={(idx % 3) * 80} className="h-full">
            <Link
              to={i.path}
              className="group flex h-full flex-col overflow-hidden rounded-2xl border border-purple-100 bg-white transition-all duration-300 hover:-translate-y-1.5 hover:border-[#7C3AED] hover:shadow-[0_20px_40px_-18px_rgba(124,58,237,0.55)]"
            >
              <span className="relative block h-44 overflow-hidden bg-gradient-to-br from-[#7C3AED] to-[#DB2777]">
                {imageFor(i.name) ? (
                  <img
                    src={imageFor(i.name)}
                    alt={g.label(i)}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                  />
                ) : (
                  <span className="absolute inset-0 flex items-center justify-center text-5xl text-white/90">
                    <g.Icon aria-hidden="true" />
                  </span>
                )}
                <span className="absolute inset-0 bg-gradient-to-t from-[#140832]/80 via-[#140832]/10 to-transparent" aria-hidden="true" />
                <span className="absolute bottom-3 left-4 right-4 font-bold leading-snug text-white">{g.label(i)}</span>
              </span>
              <span className="flex flex-1 flex-col p-5">
                {i.description && <span className="block text-sm text-gray-500 leading-relaxed line-clamp-3">{i.description}</span>}
                <span className="mt-auto inline-flex items-center gap-1.5 pt-3 text-sm font-semibold text-[#DB2777]">
                  View jobs <FaArrowRight className="text-xs group-hover:translate-x-1.5 transition-transform motion-reduce:transition-none" aria-hidden="true" />
                </span>
              </span>
            </Link>
          </Reveal>
        </li>
      ))}
    </ul>
  </section>
))}

{/* CTA */}
<Reveal variant="zoom">
  <section className="animated-gradient relative overflow-hidden rounded-3xl px-7 lg:px-12 py-9 flex flex-col lg:flex-row items-center justify-between gap-5 text-white shadow-[0_30px_60px_-25px_rgba(124,58,237,0.6)]">
    <div className="absolute inset-0 opacity-15 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '22px 22px' }} aria-hidden="true" />
    <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/20 blur-3xl float-slow pointer-events-none" aria-hidden="true" />
    <div className="relative text-center lg:text-left">
      <h2 className="text-2xl font-bold mb-1">Can’t find what you need?</h2>
      <p className="text-white/85 text-sm">Search every opening with your own filters, or create a profile to get matched.</p>
    </div>
    <div className="relative flex flex-wrap justify-center gap-3">
      <Link to="/jobs" className="bg-white text-[#7C3AED] px-7 py-3 rounded-xl font-bold text-sm transition hover:-translate-y-0.5 hover:shadow-lg">Browse all jobs</Link>
      <Link to="/register" className="border border-white/50 px-7 py-3 rounded-xl font-semibold text-sm transition hover:-translate-y-0.5 hover:bg-white/10">Create free profile</Link>
    </div>
  </section>
</Reveal>
      </main>

      <style>{`
  .brand-gradient{background-image:linear-gradient(90deg,#7C3AED,#DB2777)}
  .group:hover .group-hover\\:brand-gradient{background-image:linear-gradient(90deg,#7C3AED,#DB2777)}

  .animated-gradient{background:linear-gradient(90deg,#7C3AED,#DB2777,#7C3AED);background-size:200% 100%;animation:gradient-shift 8s ease infinite}

  .reveal{opacity:0;transform:translateY(28px);transition:opacity .7s cubic-bezier(.2,.8,.2,1),transform .7s cubic-bezier(.2,.8,.2,1)}
  .reveal-zoom{transform:scale(.94)}
  .reveal-in{opacity:1;transform:none}

  @keyframes rise{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:none}}
  @keyframes float{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-24px) scale(1.06)}}
  @keyframes shimmer{to{background-position:200% center}}
  @keyframes gradient-shift{0%,100%{background-position:0% 50%}50%{background-position:100% 50%}}
  @keyframes bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}

  .rise{animation:rise .8s cubic-bezier(.2,.8,.2,1) both}
  .float-slow{animation:float 9s ease-in-out infinite}
  .float-slower{animation:float 13s ease-in-out infinite reverse}
  .shimmer-text{background-size:200% auto;animation:shimmer 5s linear infinite}
  .bob{animation:bob 3s ease-in-out infinite}

  .icon-wiggle{transition:transform .4s ease,background-color .3s ease,color .3s ease}
  .group:hover .icon-wiggle{transform:rotate(-10deg) scale(1.15)}

  @media (prefers-reduced-motion: reduce){
    .rise,.float-slow,.float-slower,.shimmer-text,.bob,.animated-gradient{animation:none}
    .reveal{opacity:1;transform:none;transition:none}
    .icon-wiggle{transition:none}
  }
`}</style>
      <Footer />
    </div>
  );
}

export default Hub;

export const seoHubRoutes = HUBS.map((h) => (
  <Route key={h.path} path={h.path} element={<Hub hub={h} />} />
));